import { FunctionComponent, useState, useEffect } from 'react';
import { Form, InputGroup, Row, Col } from 'react-bootstrap';
import { useTranslate } from '@xFrame4/components/Hooks';

export interface ColorPickerProps
{
    /** The name/field identifier for the color picker */
    field: string;
    /** The label text for the color picker */
    label?: string;
    /** Current color value in hex format (e.g., '#007bff') */
    value?: string;
    /** Default color to show when value is empty */
    defaultValue?: string;
    /** Whether the field is required */
    isRequired?: boolean;
    /** Whether the field is enabled/disabled */
    isEnabled?: boolean;
    /** Whether to show the hex input field alongside the color picker */
    showHexInput?: boolean;
    /** Placeholder text for hex input */
    placeholder?: string;
    /** CSS class for the container */
    className?: string;
    /** Invalid feedback message for validation errors */
    invalidFeedbackMessage?: string;
    /** Callback when color value changes */
    onValueChanged?: (field: string, value: string) => void;
}

/**
 * ColorPicker component for selecting colors with hex input support
 * Provides both native color picker (which acts as visual preview) and manual hex input with real-time synchronization
 */
const ColorPicker: FunctionComponent<ColorPickerProps> = (props) =>
{
    const t = useTranslate();
    const {
        field,
        label,
        value,
        defaultValue = '#007bff',
        isRequired = false,
        isEnabled = true,
        showHexInput = true,
        placeholder,
        className = '',
        invalidFeedbackMessage,
        onValueChanged
    } = props;

    const [hexInput, setHexInput] = useState(value ?? defaultValue);
    const [isValidHex, setIsValidHex] = useState(true);

    /** Validate hex color format */
    const validateHexColor = (color: string): boolean =>
    {
        const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
        return hexPattern.test(color);
    };

    /** Convert 3-digit hex to 6-digit hex */
    const expandHex = (hex: string): string =>
    {
        if (hex.length === 4)
        {
            return '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3];
        }
        return hex;
    };

    /** Handle color picker change */
    const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    {
        const newColor = event.target.value;
        setHexInput(newColor);
        setIsValidHex(true);
        
        if (onValueChanged)
        {
            onValueChanged(field, newColor);
        }
    };

    /** Handle hex input change */
    const handleHexInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    {
        let newHex = event.target.value;
        
        // Add # if missing
        if (newHex && !newHex.startsWith('#'))
        {
            newHex = '#' + newHex;
        }
        
        setHexInput(newHex);
        
        if (validateHexColor(newHex))
        {
            setIsValidHex(true);
            const expandedHex = expandHex(newHex);
            
            if (onValueChanged)
            {
                onValueChanged(field, expandedHex);
            }
        }
        else
        {
            setIsValidHex(false);
        }
    };

    /** Update hex input when value prop changes */
    useEffect(() =>
    {
        if (value !== undefined && value !== hexInput)
        {
            setHexInput(value);
            setIsValidHex(validateHexColor(value));
        }
    }, [value]);


    const currentColor = value ?? defaultValue;
    const hasError = invalidFeedbackMessage || (!isValidHex && hexInput !== '');
    const errorMessage = invalidFeedbackMessage ?? (isValidHex ? '' : t('INVALID_COLOR_FORMAT'));

    return (
        <Form.Group className={`mb-3 ${className}`}>
            {label && (
                <Form.Label>
                    {label}
                    {isRequired && <span className='text-danger ms-1'>*</span>}
                </Form.Label>
            )}
            
            <Row className='g-2'>
                <Col xs='auto'>
                    <Form.Control
                        type='color'
                        value={currentColor}
                        disabled={!isEnabled}
                        onChange={handleColorPickerChange}
                        className={hasError ? 'is-invalid' : ''}
                        style={{ 
                            width: '60px', 
                            height: '38px', 
                            padding: '2px',
                            cursor: 'pointer',
                            border: 'none',
                            borderRadius: '4px'
                        }}
                        aria-label={`${label ?? t('COLOR_PICKER')} color picker`}
                    />
                </Col>
                
                
                {showHexInput && (
                    <Col>
                        <InputGroup hasValidation>
                            <InputGroup.Text>#</InputGroup.Text>
                            <Form.Control
                                type='text'
                                value={hexInput.startsWith('#') ? hexInput.substring(1) : hexInput}
                                placeholder={placeholder ?? 'RRGGBB'}
                                disabled={!isEnabled}
                                onChange={handleHexInputChange}
                                className={hasError ? 'is-invalid' : ''}
                                aria-label={`${label ?? t('COLOR_PICKER')} hex input`}
                                maxLength={6}
                                autoComplete='off'
                                spellCheck='false'
                                style={{ textTransform: 'uppercase' }}
                            />
                            <Form.Control.Feedback type='invalid'>
                                {errorMessage}
                            </Form.Control.Feedback>
                        </InputGroup>
                    </Col>
                )}
            </Row>
            
            {!showHexInput && hasError && (
                <Form.Control.Feedback type='invalid' className='d-block'>
                    {errorMessage}
                </Form.Control.Feedback>
            )}
        </Form.Group>
    );
};

export default ColorPicker;